import React, { useState, useEffect } from 'react';
import { Plus, Edit } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { API_BASE } from './doctor-management/constants';
import { DoctorDeleteDialog } from './doctor-management/DoctorDeleteDialog';
import { DoctorEditDialog } from './doctor-management/DoctorEditDialog';
import { DoctorCreateDialog } from './doctor-management/DoctorCreateDialog';
import { DoctorList } from './doctor-management/DoctorList';
import { DoctorFilters } from './doctor-management/DoctorFilters';
import { DoctorStatsCards } from './doctor-management/DoctorStatsCards';
export default function DoctorManagementFull() {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    phone: '',
    specialization: '',
    experience_years: '',
    qualification: '',
    hospital_affiliation: '',
    consultation_fee: '',
    city: '',
    state: '',
    bio: ''
  });
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    specialization: 'all'
  });

  const specializations = [
    'Cardiology', 'Orthopedics', 'Neurology', 'Oncology', 'Dermatology',
    'Gastroenterology', 'Pulmonology', 'Nephrology', 'Urology', 'Ophthalmology',
    'ENT', 'Psychiatry', 'Pediatrics', 'Gynecology', 'General Surgery',
    'Plastic Surgery', 'Dental', 'Ayurveda', 'Homeopathy'
  ];

  useEffect(() => {
    fetchDoctors();
  }, []);

  const fetchDoctors = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/doctors-management`, {
        headers: { }
      });

      if (response.ok) {
        const data = await response.json();
        setDoctors(data.doctors || []);
      }
    } catch (err) {
      toast.error('Failed to fetch doctors');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/doctors-management`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Doctor created successfully');
        setShowCreateDialog(false);
        resetForm();
        fetchDoctors();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create doctor');
      }
    } catch (err) {
      toast.error('Failed to create doctor');
    }
  };

  const handleUpdate = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/doctors-management/${selectedDoctor._id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Doctor updated successfully');
        setShowEditDialog(false);
        resetForm();
        fetchDoctors();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update doctor');
      }
    } catch (err) {
      toast.error('Failed to update doctor');
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/doctors-management/${selectedDoctor._id}`, {
        method: 'DELETE',
        headers: { }
      });

      if (response.ok) {
        toast.success('Doctor deleted successfully');
        setShowDeleteDialog(false);
        setSelectedDoctor(null);
        fetchDoctors();
      } else {
        toast.error('Failed to delete doctor');
      }
    } catch (err) {
      toast.error('Failed to delete doctor');
    }
  };

  const handleStatusChange = async (doctorId, newStatus) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/doctors-management/${doctorId}/status`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success(`Doctor ${newStatus} successfully`);
        fetchDoctors();
      }
    } catch (err) {
      toast.error('Failed to update doctor status');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      full_name: '',
      phone: '',
      specialization: '',
      experience_years: '',
      qualification: '',
      hospital_affiliation: '',
      consultation_fee: '',
      city: '',
      state: '',
      bio: ''
    });
    setSelectedDoctor(null);
  };

  const openEditDialog = (doctor) => {
    setSelectedDoctor(doctor);
    setFormData({
      email: doctor.email || '',
      full_name: doctor.full_name || '',
      phone: doctor.phone || '',
      specialization: doctor.specialization || '',
      experience_years: doctor.experience_years || '',
      qualification: doctor.qualification || '',
      hospital_affiliation: doctor.hospital_affiliation || '',
      consultation_fee: doctor.consultation_fee || '',
      city: doctor.city || '',
      state: doctor.state || '',
      bio: doctor.bio || ''
    });
    setShowEditDialog(true);
  };

  const filteredDoctors = doctors.filter(doc => {
    const matchesSearch = filters.search === '' || 
      doc.full_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
      doc.email?.toLowerCase().includes(filters.search.toLowerCase());
    const matchesStatus = filters.status === 'all' || doc.account_status === filters.status;
    const matchesSpec = filters.specialization === 'all' || doc.specialization === filters.specialization;
    return matchesSearch && matchesStatus && matchesSpec;
  });

  const getStatusBadge = (status) => {
    const styles = {
      verified: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      suspended: 'bg-red-100 text-red-800',
      rejected: 'bg-gray-100 text-gray-800'
    };
    return styles[status] || styles.pending;
  };

  const stats = {
    total: doctors.length,
    verified: doctors.filter(d => d.account_status === 'verified').length,
    pending: doctors.filter(d => d.account_status === 'pending').length,
    suspended: doctors.filter(d => d.account_status === 'suspended').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading doctors...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="doctor-management-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Doctor Management</h1>
          <p className="text-gray-600 mt-1">Manage and verify doctor accounts</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-purple-600 hover:bg-purple-700" data-testid="add-doctor-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Doctor
        </Button>
      </div>

      {/* Stats Cards */}
      <DoctorStatsCards stats={stats} />

      {/* Filters */}
      <DoctorFilters doctors={doctors} filters={filters} setFilters={setFilters} specializations={specializations} />

      {/* Doctors List */}
      <DoctorList doctors={doctors} filteredDoctors={filteredDoctors} getStatusBadge={getStatusBadge} handleStatusChange={handleStatusChange} openEditDialog={openEditDialog} setSelectedDoctor={setSelectedDoctor} setShowDeleteDialog={setShowDeleteDialog} />

      {/* Create Doctor Dialog */}
      <DoctorCreateDialog formData={formData} handleCreate={handleCreate} resetForm={resetForm} setFormData={setFormData} setShowCreateDialog={setShowCreateDialog} showCreateDialog={showCreateDialog} specializations={specializations} />

      {/* Edit Doctor Dialog */}
      <DoctorEditDialog formData={formData} handleUpdate={handleUpdate} resetForm={resetForm} setFormData={setFormData} setShowEditDialog={setShowEditDialog} showEditDialog={showEditDialog} specializations={specializations} />

      {/* Delete Confirmation Dialog */}
      <DoctorDeleteDialog handleDelete={handleDelete} selectedDoctor={selectedDoctor} setShowDeleteDialog={setShowDeleteDialog} showDeleteDialog={showDeleteDialog} />
    </div>
  );
}
